import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const translations = {
  fr: {
    title: "🍪 Cookies",
    description: "Nous utilisons des cookies pour améliorer votre expérience sur notre site.",
    learnMore: "En savoir plus",
    accept: "Accepter",
    decline: "Refuser",
    customize: "Personnaliser",
    necessary: "Cookies nécessaires",
    necessaryDesc: "Ces cookies sont essentiels au fonctionnement du site.",
    analytics: "Cookies analytiques",
    analyticsDesc: "Nous aident à comprendre comment vous utilisez le site.",
    marketing: "Cookies marketing",
    marketingDesc: "Utilisés pour vous proposer des publicités pertinentes.",
    save: "Sauvegarder mes préférences"
  },
  en: {
    title: "🍪 Cookies",
    description: "We use cookies to enhance your experience on our site.",
    learnMore: "Learn more",
    accept: "Accept",
    decline: "Decline",
    customize: "Customize",
    necessary: "Necessary cookies",
    necessaryDesc: "These cookies are essential for the site to function.",
    analytics: "Analytics cookies",
    analyticsDesc: "Help us understand how you use the site.",
    marketing: "Marketing cookies",
    marketingDesc: "Used to show you relevant advertisements.",
    save: "Save my preferences"
  }
};

const COOKIE_CONSENT_KEY = "azurys_cookie_consent";

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const { lang } = useLanguage();
  const t = translations[lang];

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { necessary: true, analytics: true, marketing: true, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleDecline = () => {
    const consent = { necessary: true, analytics: false, marketing: false, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = { ...preferences, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50"
        >
          <div className="bg-card/95 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 shadow-2xl">
            {!showCustomize ? (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-1">
                    <h3 className="text-foreground font-semibold text-base mb-2">{t.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {t.description}{" "}
                      <Link to="/privacy" className="text-primary hover:underline">
                        {t.learnMore}
                      </Link>
                    </p>
                  </div>
                  <button
                    onClick={() => setIsVisible(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    aria-label="Fermer"
                  >
                    <X size={18} />
                  </button>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {t.accept}
                  </Button>
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    className="flex-1 border-white/10 hover:bg-white/5"
                  >
                    {t.decline}
                  </Button>
                  <Button
                    onClick={() => setShowCustomize(true)}
                    variant="ghost"
                    className="flex-1 text-muted-foreground hover:text-foreground"
                  >
                    {t.customize}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-foreground font-semibold text-base">{t.title}</h3>
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    aria-label="Retour"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-4 mb-5">
                  {/* Necessary - always on */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 accent-primary"
                    />
                    <div>
                      <p className="text-foreground text-sm font-medium">{t.necessary}</p>
                      <p className="text-muted-foreground text-xs">{t.necessaryDesc}</p>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="mt-1 accent-primary cursor-pointer"
                    />
                    <div>
                      <p className="text-foreground text-sm font-medium">{t.analytics}</p>
                      <p className="text-muted-foreground text-xs">{t.analyticsDesc}</p>
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="mt-1 accent-primary cursor-pointer"
                    />
                    <div>
                      <p className="text-foreground text-sm font-medium">{t.marketing}</p>
                      <p className="text-muted-foreground text-xs">{t.marketingDesc}</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSavePreferences}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {t.save}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
